import type { FileContext } from "api-contracts/rpc/getFileUploadUrl";
import { rpc } from "./rpc";

import { getAssetUrl } from "helpers/assetHelpers";
import { uploadFileInChunks } from "./chunk-upload";
import { validateFile, CHAT_ATTACHMENT_MIME_TYPES, CHAT_ATTACHMENT_MAX_SIZE_BYTES } from "helpers/fileValidation";

export interface UploadOptions {
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
  expiresInMinutes?: number;
  positionXPercentage?: number;
  positionYPercentage?: number;
}

export interface UploadResult {
  fileId: string;
  url: string;
  relativePath: string;
  uploadUrl: string;
}

export class FileUploadError extends Error {
  public code: "VALIDATION_ERROR" | "UPLOAD_ERROR" | "NETWORK_ERROR" | "ABORT_ERROR";
  public originalError?: Error;

  constructor(
    message: string,
    code: "VALIDATION_ERROR" | "UPLOAD_ERROR" | "NETWORK_ERROR" | "ABORT_ERROR",
    originalError?: Error,
  ) {
    super(message);
    this.name = "FileUploadError";
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Context-specific validation rules for different file upload contexts
 */
const CONTEXT_VALIDATION_RULES: Record<
  FileContext,
  {
    maxSize: number;
    allowedTypes: string[];
    description: string;
  }
> = {
  profile_image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/avif"],
    description: "profile image",
  },
  cover_image: {
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/avif", "video/mp4", "video/webm", "video/quicktime"],
    description: "cover image",
  },
  brand_asset: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/avif"],
    description: "brand asset",
  },
  bio_link: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/avif"],
    description: "bio link image",
  },
  email_asset: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/avif"],
    description: "email asset",
  },
  chat_attachment: {
    maxSize: CHAT_ATTACHMENT_MAX_SIZE_BYTES,
    allowedTypes: [...CHAT_ATTACHMENT_MIME_TYPES],
    description: "chat attachment",
  },
};

/**
 * Validate file against context-specific rules
 */
function validateFileForContext(file: File, context: FileContext): void {
  // First run basic validation
  const basicValidation = validateFile({
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
  });

  if (!basicValidation.isValid) {
    throw new FileUploadError(basicValidation.error || "File validation failed", "VALIDATION_ERROR");
  }

  // Then run context-specific validation
  const rules = CONTEXT_VALIDATION_RULES[context];

  if (file.size > rules.maxSize) {
    const maxSizeMB = Math.round(rules.maxSize / 1024 / 1024);
    throw new FileUploadError(
      `File size too large for ${rules.description}. Maximum: ${maxSizeMB}MB`,
      "VALIDATION_ERROR",
    );
  }

  if (!rules.allowedTypes.includes(file.type)) {
    throw new FileUploadError(`File type ${file.type} not allowed for ${rules.description}`, "VALIDATION_ERROR");
  }
}

/**
 * Network connection interface for the Network Information API
 */
interface NetworkConnection {
  effectiveType?: string;
  type?: string;
  downlink?: number;
}

/**
 * Extended Navigator interface with connection property
 */
interface NavigatorWithConnection extends Navigator {
  connection?: NetworkConnection;
}

/**
 * Detect if user is on mobile network
 */
function isMobileConnection(): boolean {
  // Check if navigator.connection is available (Chrome, Edge, Opera)
  if ("connection" in navigator) {
    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection) {
      const mobileTypes = ["cellular", "2g", "3g", "4g", "5g"];
      return (
        mobileTypes.includes(connection.effectiveType || "") ||
        mobileTypes.includes(connection.type || "") ||
        (connection.downlink !== undefined && connection.downlink < 1.0)
      ); // Less than 1 Mbps suggests mobile
    }
  }

  // Fallback: detect mobile user agent
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Get optimized timeout values based on connection type
 */
function getTimeoutValues(fileSize: number) {
  const isMobile = isMobileConnection();

  // More lenient timeout calculation
  // Base: 30 seconds minimum, then 10 seconds per MB (20 seconds per MB for mobile)
  const timeoutPerMB = isMobile ? 20000 : 10000;
  const baseTimeout = 30000; // 30 seconds minimum
  const sizeBasedTimeout = baseTimeout + (fileSize / (1024 * 1024)) * timeoutPerMB;

  return {
    timeout: Math.min(sizeBasedTimeout, 600000), // Max 10 minutes (increased from 5)
    isMobile,
  };
}

/**
 * Upload file to Azure Storage with progress tracking and mobile optimizations
 */
async function uploadFileToAzure(file: File, uploadUrl: string, options?: UploadOptions): Promise<void> {
  const maxRetries = 2; // Allow up to 2 retries for mobile connections
  let attempt = 0;

  const attemptUpload = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const { timeout, isMobile } = getTimeoutValues(file.size);

      // Set optimized timeout
      xhr.timeout = timeout;

      // Handle abort signal
      if (options?.signal) {
        options.signal.addEventListener("abort", () => {
          xhr.abort();
          reject(new FileUploadError("Upload aborted", "ABORT_ERROR"));
        });
      }

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && options?.onProgress) {
          const progress = (event.loaded / event.total) * 100;
          options.onProgress(progress);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const errorMessage =
            isMobile && xhr.status === 0
              ? "Upload failed - please check your mobile connection and try again"
              : `Upload failed with status ${xhr.status}: ${xhr.statusText}`;
          reject(new FileUploadError(errorMessage, "UPLOAD_ERROR"));
        }
      });

      xhr.addEventListener("error", () => {
        const errorMessage = isMobile
          ? "Network error during upload - mobile connection may be unstable. Please try again."
          : "Network error during upload";
        reject(new FileUploadError(errorMessage, "NETWORK_ERROR"));
      });

      xhr.addEventListener("timeout", () => {
        const errorMessage = isMobile
          ? "Upload timed out - large files may take longer on mobile networks. Please try again."
          : "Upload timed out - please try again";
        reject(new FileUploadError(errorMessage, "NETWORK_ERROR"));
      });

      xhr.open("PUT", uploadUrl);

      // Set required headers for Azure Storage
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

      // Mobile optimizations
      if (isMobile) {
        xhr.setRequestHeader("Connection", "keep-alive");
      }

      // Send the file
      xhr.send(file);
    });
  };

  // Retry logic for mobile connections
  while (attempt < maxRetries + 1) {
    try {
      await attemptUpload();
      return; // Success - exit retry loop
    } catch (error) {
      attempt++;

      // Don't retry on validation errors or user abort
      if (error instanceof FileUploadError && (error.code === "VALIDATION_ERROR" || error.code === "ABORT_ERROR")) {
        throw error;
      }

      // Don't retry if we've exhausted attempts
      if (attempt > maxRetries) {
        throw error;
      }

      // Only retry on network/timeout errors for mobile
      const { isMobile } = getTimeoutValues(file.size);
      if (
        !isMobile ||
        (error instanceof FileUploadError && error.code !== "NETWORK_ERROR" && error.code !== "UPLOAD_ERROR")
      ) {
        throw error;
      }

      // Wait before retry (exponential backoff: 1s, 2s)
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
}

/**
 * Main file upload function with context-aware validation and progress tracking
 */
export async function uploadFile(file: File, context: FileContext, options?: UploadOptions) {
  try {
    // Validate file for the specific context
    validateFileForContext(file, context);

    // Get upload URL from API using typed RPC
    const result = await rpc("getFileUploadUrl", {
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      context,
      expiresInMinutes: options?.expiresInMinutes || 60,
      ...(options?.positionXPercentage !== undefined && { positionXPercentage: options.positionXPercentage }),
      ...(options?.positionYPercentage !== undefined && { positionYPercentage: options.positionYPercentage }),
    });

    const { uploadUrl, relativePath, fileId } = result;

    // Smart upload selection: use chunked upload for files >= 2MB
    const CHUNK_THRESHOLD = 2 * 1024 * 1024; // 2MB

    // Transform URL consistently for both upload methods
    const url = getAssetUrl(uploadUrl) || uploadUrl;

    if (file.size >= CHUNK_THRESHOLD) {
      // Use chunked upload for large files (better reliability)
      await uploadFileInChunks({
        file,
        sasUrl: url, // Use transformed URL
        blockSize: 2 * 1024 * 1024, // 2MB chunks
        onProgress: options?.onProgress,
        signal: options?.signal,
      });
    } else {
      // Use single upload for small files (faster)
      await uploadFileToAzure(file, url, options);
    }

    return {
      fileId,
      url: getAssetUrl(relativePath) || relativePath,
    };
  } catch (error) {
    if (error instanceof FileUploadError) {
      throw error;
    }

    // Wrap unknown errors
    throw new FileUploadError(
      error instanceof Error ? error.message : "Unknown upload error",
      "UPLOAD_ERROR",
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Create an AbortController for canceling uploads
 */
export function createUploadController(): AbortController {
  return new AbortController();
}

/**
 * Helper function to format file upload errors for user display
 */
export function formatUploadError(error: FileUploadError): string {
  switch (error.code) {
    case "VALIDATION_ERROR":
      return error.message;
    case "UPLOAD_ERROR":
      return "Upload failed. Please try again.";
    case "NETWORK_ERROR":
      return "Network error occurred. Please check your connection and try again.";
    case "ABORT_ERROR":
      return "Upload was canceled.";
    default:
      return "An unexpected error occurred during upload.";
  }
}

export function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(",");
  const mime = arr[0]?.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const bstr = atob(arr[1] || "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
